import React, {FC, useState, useEffect} from "react";
import HotelRoom from "../../../hotels/types/HotelRoom";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import useCustomerContext from "../../../customers/hooks/useCustomerContext";
import ModalRoomImagesGallery from "../../../hotelEventRoom/components/ModalRoomImagesGallery";
import moment from "moment";
import ButtonPrimary from "../../../ui/shared/Button/ButtonPrimary";
import Prebooking from "../../../booking/types/Prebooking";
import RoomHotelEventCardImage from "../../../hotelEventRoom/components/RoomHotelEventCardImage";
import HotelWizardRoomFeedbackModal from "../modals/HotelWizardRoomFeedbackModal";
import HotelWizardRoomDateSelectorModal from "../modals/HotelWizardRoomDateSelectorModal";
import NcInputNumber from "../../../ui/components/NcInputNumber/NcInputNumber";
import {formatNumber} from "../../../common/utils/number";
import EventCity from "../../../eventCities/types/EventCity";
import {useRoomCardLogic} from "../../hooks/useRoomCardLogic";
import {getRoomImages} from "../../../common/utils/getRoomImages";
import BasketItem from "../../../booking/types/BasketItem";
import {useIsRoomSelected} from "../../hooks/useIsRoomSelected";
import useDeletePrebookingRoom from "../../../booking/hooks/useDeletePrebookingRoom";

export interface HotelWebRoomCardProps {
  preBooking: Prebooking;
  hotelRoom: HotelRoom;
  roomPrice?: number;
  eventCity: EventCity;
  preBookingHotelRooms?: BasketItem[];
  currencySymbol: string;
}

const HotelWebRoomCard: FC<HotelWebRoomCardProps> = ({
                                                       preBooking,
                                                       hotelRoom,
                                                       roomPrice,
                                                       eventCity,
                                                       preBookingHotelRooms,
                                                       currencySymbol,
                                                     }) => {
  const t = useTranslate();
  const {languageCode} = useCustomerContext();
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);
  const isSelected = useIsRoomSelected(hotelRoom.id, preBookingHotelRooms);
  const {save: deleteItem} = useDeletePrebookingRoom();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [price, setPrice] = useState(roomPrice);

  const {
    quantity,
    setQuantity,
    isDateSelectorOpen,
    setIsDateSelectorOpen,
    modalIsOpen,
    setModalIsOpen,
    isCheckingAvailability,
    isAddingRoomLoading,
    unavailableDates,
    overbookingDates,
    errorMessage,
    handleDatesConfirmed,
    handleConfirmAddRoom,
    modalKind,
  } = useRoomCardLogic({
    preBookingInternalId: preBooking.internalId,
    hotelRoom,
    startDate: eventCity.startDate,
    endDate: eventCity.endDate,
  });

  // Delete room logic
  const handleDeleteRoom = () => {
    setIsDeletingRoom(true);
    // Find the prebooking room that matches this hotelRoom
    const matchingRoom = preBookingHotelRooms?.find(room =>
      room.roomHotelEvent === `/room_hotel_events/${hotelRoom.id}`
    );

    if (!matchingRoom) {
      setIsDeletingRoom(false);
      return;
    }

    deleteItem({
      prebookingInternalId: preBooking.internalId || "",
      itemId: matchingRoom?.id || 0,
    }, {
      onSuccess: () => {
        setIsDeletingRoom(false);
      },
      onError: () => {
        setIsDeletingRoom(false);
      }
    });
  };
  // ... Actualizar precio cuando cambia cantidad
  useEffect(() => {
    if (roomPrice) {
      setPrice(roomPrice * quantity);
    }
  }, [quantity, roomPrice]);

  // Map images to use principalImgContentUrl when contentUrl is null
  const roomImages = getRoomImages(hotelRoom);

  return (
    <div className="listingSectionSidebar__wrap shadow-xl mb-5">
      {/* TITLE */}
      <div className="flex justify-between mb-4 items-center">
        <span className="text-1xl">
          <i className="text-2xl las la-bed la-fw" style={{marginRight: "5px"}}></i> {hotelRoom.trType[languageCode]}
        </span>
      </div>
      {/* CONTENT WITH IMAGE - Use flex layout when image exists */}
      <div className="flex gap-3">
        {/* IMAGE */}
        {hotelRoom.principalImgContentUrl && (
          <RoomHotelEventCardImage hotelRoom={hotelRoom} roomImages={roomImages} setIsGalleryOpen={setIsGalleryOpen}/>
        )}
        {/* INFORMATION */}
        <div className="flex flex-col space-y-3 flex-1">
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.from")}</span>
            <span>{moment(eventCity.startDate).format("DD/MM/YYYY")}</span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.to")}</span>
            <span>{moment(eventCity.endDate).format("DD/MM/YYYY")}</span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.capacity")}</span>
            <span>
              {[...Array(hotelRoom.adults)].map((value, index) =>
                <i className="las la-user la-lg" key={index + hotelRoom.id}></i>
              )}
              {[...Array(hotelRoom.children)].map((value, index) =>
                <i className="text-xl las la-user" key={index + hotelRoom.id}></i>
              )}
            </span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.quantity")}</span>
            <NcInputNumber
              className=""
              defaultValue={quantity}
              onChange={setQuantity}
              max={10}
              min={1}
            />
          </div>

          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex justify-between font-semibold">
            <span>{t("hotelDetail.total")}</span>
            <div>{formatNumber(price || 0)} <span
              dangerouslySetInnerHTML={{__html: currencySymbol}}/></div>
          </div>
        </div>
      </div>

      {/* BUTTON */}
      <ButtonPrimary
        type="button"
        onClick={(e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (isSelected) {
            handleDeleteRoom();
          } else {
            setIsDateSelectorOpen(true);
          }
        }}
        className={`uppercase mt-4 w-full font-medium transition px-6 py-2 rounded-lg ${
          isSelected
            ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
            : 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white'}`
        }
        loading={isSelected ? isDeletingRoom : (isCheckingAvailability || isAddingRoomLoading)}
      >
        <i className={`text-2xl la-fw ${isSelected ? 'las la-trash-alt' : 'las la-shopping-cart'}`}></i>
        {isSelected ? t("common.actions.unSelect") : t("packages.fields.selectRoom")}
      </ButtonPrimary>

      {modalKind !== "roomAdded" && (
        <HotelWizardRoomFeedbackModal
          kind={modalKind}
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false);
          }}
          onConfirmAddRoom={handleConfirmAddRoom}
          overbookingDates={overbookingDates}
          unavailableDates={unavailableDates}
          errorMessage={errorMessage}
        />
      )}

      {/* Date Selector Modal */}
      <HotelWizardRoomDateSelectorModal
        isOpen={isDateSelectorOpen}
        onClose={() => setIsDateSelectorOpen(false)}
        onConfirm={handleDatesConfirmed}
        minStartDate={eventCity.startDate || ""}
        maxEndDate={eventCity.endDate || ""}
        eventCity={eventCity}
      />

      {/* ROOM IMAGES GALLERY MODAL */}
      {roomImages.length > 0 && (
        <ModalRoomImagesGallery
          roomImages={roomImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          initFocus={0}
        />
      )}
    </div>
  );
};

export default HotelWebRoomCard;


